// src/pages/DraftWars/DraftWarsPanel.jsx
// Draft Wars admin content — embedded inside the main AdminPage.
// Assumes the user is already authenticated (AdminPage enforces that).

import { useEffect, useState, useMemo, useRef } from "react";
import { onSnapshot, query, orderBy, limit } from "firebase/firestore";
import {
  FaTrophy, FaBolt, FaRandom, FaCheck, FaTimes,
  FaCopy, FaRedoAlt, FaGavel, FaUsers, FaLayerGroup,
  FaEye, FaUndo, FaPlay,
} from "react-icons/fa";
import {
  refs, createSession, regenerateCaptainToken, regenerateAllTokens,
  nominatePlayer, randomNominate, cancelNomination, confirmWinner,
  startDraft, startBidding, completeDraft, undoLastSale,
} from "./draftWarsEngine";
import { CAPTAIN_IMAGES, PLAYER_IMAGES, TIER_META } from "./draftWarsData";
import { useCountdown, useServerTimeSync } from "./draftWarsHooks";

const fmt = (n) => "$" + (n ?? 0).toLocaleString();
const originForLinks = () => typeof window !== "undefined" ? window.location.origin : "";

function DraftWarsPanel() {
  useServerTimeSync();
  const [session, setSession] = useState(null);
  const [captains, setCaptains] = useState([]);
  const [players, setPlayers] = useState([]);
  const [bids, setBids] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [lastSale, setLastSale] = useState(null);
  const [manualPickOpen, setManualPickOpen] = useState(false);

  // Subscriptions
  useEffect(() => onSnapshot(refs.session, (s) => setSession(s.exists() ? s.data() : null)), []);
  useEffect(() => onSnapshot(refs.captains, (s) => setCaptains(s.docs.map((d) => ({ id: d.id, ...d.data() })))), []);
  useEffect(() => onSnapshot(refs.players, (s) => setPlayers(s.docs.map((d) => ({ id: d.id, ...d.data() })))), []);
  useEffect(() => {
    const q = query(refs.bids, orderBy("at", "desc"), limit(20));
    return onSnapshot(q, (s) => setBids(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const flash = (msg, ms = 3500) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), ms);
  };

  // ── Actions ──────────────────────────────────────────────────────────
  const handleCreateSession = async () => {
    if (!window.confirm("Create a new Draft Wars session? This WIPES any existing data.")) return;
    setBusy(true);
    try { await createSession(); flash("✅ Session created with fresh captains + players"); }
    catch (e) { flash("Error: " + e.message); }
    finally { setBusy(false); }
  };

  const handleStartDraft = async () => {
    setBusy(true);
    try { await startDraft(); flash("🎬 Draft is LIVE"); }
    catch (e) { flash("Error: " + e.message); }
    finally { setBusy(false); }
  };

  const handleCompleteDraft = async () => {
    if (!window.confirm("Mark the draft as complete?")) return;
    setBusy(true);
    try { await completeDraft(); flash("🏁 Draft complete"); }
    catch (e) { flash("Error: " + e.message); }
    finally { setBusy(false); }
  };

  const handleRandomNominate = async () => {
    setBusy(true);
    try {
      const p = await randomNominate();
      flash(`🎲 Random pick: ${p.name} (${p.tier}-tier · ${fmt(p.basePrice)})`);
    } catch (e) { flash(e.message); }
    finally { setBusy(false); }
  };

  const handleManualNominate = async (playerId) => {
    setBusy(true);
    try {
      const p = await nominatePlayer(playerId);
      flash(`🎯 Nominated ${p.name}`);
      setManualPickOpen(false);
    } catch (e) { flash(e.message); }
    finally { setBusy(false); }
  };

  const handleCancelNomination = async () => {
    if (!window.confirm("Cancel this auction? Player returns to the pool.")) return;
    setBusy(true);
    try { await cancelNomination(); flash("Cancelled — player back in pool"); }
    catch (e) { flash(e.message); }
    finally { setBusy(false); }
  };

  const handleConfirmWinner = async () => {
    setBusy(true);
    try {
      const r = await confirmWinner();
      if (r.unsold) {
        flash("⚠️ No bids — player returned to pool");
      } else {
        const captain = captains.find((c) => c.id === r.captainId);
        const player = players.find((p) => p.id === r.playerId);
        flash(`✅ ${player?.name} → ${captain?.name} for ${fmt(r.amount)}`);
        setLastSale(r);
      }
    } catch (e) { flash(e.message); }
    finally { setBusy(false); }
  };

  const handleStartBidding = async () => {
    setBusy(true);
    try { await startBidding(); flash("🚀 Bidding is OPEN"); }
    catch (e) { flash(e.message); }
    finally { setBusy(false); }
  };

  const handleUndo = async () => {
    if (!lastSale || !window.confirm("Undo last sale?")) return;
    setBusy(true);
    try {
      await undoLastSale(lastSale.playerId, lastSale.captainId, lastSale.amount);
      flash("↩️ Sale undone");
      setLastSale(null);
    } catch (e) { flash(e.message); }
    finally { setBusy(false); }
  };

  const handleRegenAllTokens = async () => {
    if (!window.confirm("Regenerate ALL captain tokens? Old links will stop working.")) return;
    setBusy(true);
    try { await regenerateAllTokens(); flash("🔄 All tokens regenerated"); }
    finally { setBusy(false); }
  };

  const handleRegenCaptainToken = async (captainId) => {
    setBusy(true);
    try { await regenerateCaptainToken(captainId); flash("🔄 Token regenerated"); }
    finally { setBusy(false); }
  };

  const copyCaptainLink = async (captain) => {
    const url = `${originForLinks()}/draft-wars/c/${captain.token}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(captain.id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  // ── Derived ──────────────────────────────────────────────────────────
  const auction = session?.currentAuction || null;
  const secondsLeft = useCountdown(auction?.timerEndAt);
  const availablePlayers = useMemo(() => players.filter((p) => p.status === "available"), [players]);
  const soldPlayers = useMemo(() => players.filter((p) => p.status === "sold"), [players]);
  const playersByTier = useMemo(() => {
    const groups = { S: [], A: [], B: [] };
    availablePlayers.forEach((p) => { if (groups[p.tier]) groups[p.tier].push(p); });
    Object.keys(groups).forEach((t) => groups[t].sort((a, b) => b.basePrice - a.basePrice));
    return groups;
  }, [availablePlayers]);

  const sessionExists = !!session;

  // ── AUTO-CONFIRM when timer hits 0 ──────────────────────────────────
  // Only the admin panel fires this — exactly one source of truth.
  // A ref guards against double-firing (the countdown ticks 10Hz).
  const autoConfirmedPlayerIdRef = useRef(null);
  useEffect(() => {
    if (!auction) { autoConfirmedPlayerIdRef.current = null; return; }
    // Reset the guard when a NEW player is nominated
    if (autoConfirmedPlayerIdRef.current && autoConfirmedPlayerIdRef.current !== auction.playerId) {
      autoConfirmedPlayerIdRef.current = null;
    }
    // Fire once when timer reaches 0 and we have a winner
    if (
      secondsLeft === 0 &&
      auction.highBidder &&
      auction.phase === "active" &&
      autoConfirmedPlayerIdRef.current !== auction.playerId &&
      !busy
    ) {
      autoConfirmedPlayerIdRef.current = auction.playerId;
      handleConfirmWinner();
    }
  }, [secondsLeft, auction, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      {/* Flash notice */}
      {notice && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-700/20 border border-red-600/40 rounded-xl text-red-300 text-sm font-semibold">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="text-red-400 hover:text-white"><FaTimes size={12} /></button>
        </div>
      )}

      {/* Spectator link */}
      <div className="flex justify-end">
        <a href="/draft-wars" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-xs font-bold uppercase rounded-lg transition-colors">
          <FaEye size={11} /> Open Spectator View
        </a>
      </div>

      {/* Session Setup */}
      {!sessionExists && (
        <section className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center space-y-4">
          <FaTrophy className="text-red-600 mx-auto" size={32} />
          <h2 className="text-xl font-black uppercase tracking-wide">No Draft Session</h2>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            Click below to create a fresh session. Loads 8 captains + 24 players with starting budgets and tier base prices.
          </p>
          <button onClick={handleCreateSession} disabled={busy}
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold uppercase tracking-wide rounded-lg transition-colors">
            <FaBolt size={14} /> {busy ? "Working..." : "Create Session"}
          </button>
        </section>
      )}

      {sessionExists && (
        <>
          {/* Status */}
          <section className="bg-gray-900 border border-gray-800 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest ${
                session.status === "active" ? "bg-green-600/20 text-green-400 border border-green-500/40" :
                session.status === "complete" ? "bg-gray-700/50 text-gray-400 border border-gray-600" :
                "bg-amber-600/20 text-amber-400 border border-amber-500/40"
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${session.status === "active" ? "bg-green-500 animate-pulse" : "bg-gray-500"}`} />
                {session.status === "active" ? "Live" : session.status === "complete" ? "Complete" : "Setup"}
              </span>
              <div className="text-xs text-gray-500">
                <span className="text-white font-bold">{soldPlayers.length}</span> / {players.length} drafted · <span className="text-white font-bold">{availablePlayers.length}</span> remaining
              </div>
            </div>
            <div className="flex items-center gap-2">
              {session.status === "setup" && (
                <button onClick={handleStartDraft} disabled={busy}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase rounded-lg transition-colors">
                  <FaBolt size={11} /> Start Draft
                </button>
              )}
              {session.status === "active" && (
                <button onClick={handleCompleteDraft} disabled={busy}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold uppercase rounded-lg transition-colors">
                  🏁 Complete Draft
                </button>
              )}
              <button onClick={handleCreateSession} disabled={busy}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 hover:text-white text-xs font-bold uppercase rounded-lg transition-colors">
                <FaRedoAlt size={10} /> Reset
              </button>
            </div>
          </section>

          {/* Current Auction */}
          <section className="bg-gradient-to-br from-gray-900 to-gray-950 border border-red-600/30 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-black uppercase tracking-widest text-red-400 flex items-center gap-2">
                <FaGavel size={14} /> Current Auction
              </h2>
              {lastSale && (
                <button onClick={handleUndo} className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-400 transition-colors">
                  <FaUndo size={10} /> Undo last sale
                </button>
              )}
            </div>

            {!auction ? (
              <div className="text-center py-8 space-y-4">
                <p className="text-gray-500">No player on the block</p>
                {session.status === "active" && availablePlayers.length > 0 && (
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button onClick={handleRandomNominate} disabled={busy}
                      className="flex items-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold uppercase rounded-lg transition-colors">
                      <FaRandom size={14} /> Random Pick
                    </button>
                    <button onClick={() => setManualPickOpen(v => !v)}
                      className="flex items-center gap-2 px-5 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white font-bold uppercase rounded-lg transition-colors">
                      <FaLayerGroup size={14} /> Manual Pick
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2"
                    style={{
                      borderColor: (TIER_META[auction.tier]?.color || "#6B7280") + "99",
                      backgroundColor: TIER_META[auction.tier]?.color || "#374151",
                    }}>
                    {PLAYER_IMAGES[auction.playerName] ? (
                      <img src={PLAYER_IMAGES[auction.playerName]} alt={auction.playerName}
                        className="w-full h-full object-cover object-top" decoding="async" />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center font-black text-2xl bg-gradient-to-br ${TIER_META[auction.tier]?.accent || ""} text-white`}>
                        {auction.tier}
                      </div>
                    )}
                    <div className="absolute top-0.5 right-0.5 px-1 py-0 rounded text-white text-[9px] font-black shadow"
                      style={{ backgroundColor: TIER_META[auction.tier]?.color }}>
                      {auction.tier}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs uppercase tracking-wider text-gray-500">Now auctioning</span>
                      <span className="px-1.5 py-0.5 rounded text-white text-[9px] font-black tracking-widest uppercase"
                        style={{ backgroundColor: TIER_META[auction.tier]?.color }}>
                        {TIER_META[auction.tier]?.label || (auction.tier + "-Tier")}
                      </span>
                    </div>
                    <h3 className="text-3xl font-black text-white truncate">{auction.playerName}</h3>
                    <p className="text-gray-500 text-sm">Base: {fmt(auction.basePrice)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Timer</p>
                    <p className={`text-4xl font-black tabular-nums ${
                      secondsLeft === null ? "text-gray-600" :
                      secondsLeft <= 2 ? "text-red-500 animate-pulse" : "text-white"
                    }`}>
                      {secondsLeft === null ? "—" : secondsLeft + "s"}
                    </p>
                  </div>
                </div>

                <div className="bg-black/40 border border-gray-800 rounded-xl p-4">
                  {auction.phase === "nominated" ? (
                    <p className="text-center text-amber-400 text-sm font-semibold">
                      Preview — captains see this player. Click <strong>Start Bidding</strong> below to open the auction.
                    </p>
                  ) : auction.highBid ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500">High bidder</p>
                        <p className="text-xl font-bold text-white">{auction.highBidderName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-wider text-gray-500">Bid</p>
                        <p className="text-2xl font-black text-green-400 tabular-nums">{fmt(auction.highBid)}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 text-sm">Waiting for first bid · min {fmt(auction.basePrice)}</p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {auction.phase === "nominated" ? (
                    <button onClick={handleStartBidding} disabled={busy}
                      className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white font-black uppercase tracking-wide rounded-lg transition-colors shadow-lg shadow-green-600/30">
                      <FaPlay size={12} /> Start Bidding
                    </button>
                  ) : (
                    <button onClick={handleConfirmWinner} disabled={busy}
                      className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 font-bold uppercase rounded-lg transition-colors ${
                        auction.phase === "active" && secondsLeft === 0
                          ? "bg-green-600 hover:bg-green-700 text-white animate-pulse"
                          : "bg-gray-800 hover:bg-gray-700 text-white border border-gray-700"
                      }`}>
                      <FaCheck size={12} /> Confirm Winner
                    </button>
                  )}
                  <button onClick={handleCancelNomination} disabled={busy}
                    className="px-5 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 hover:text-white font-bold uppercase text-xs rounded-lg transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Manual pick */}
          {manualPickOpen && !auction && (
            <section className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-300">Choose a player</h3>
                <button onClick={() => setManualPickOpen(false)} className="text-gray-500 hover:text-white"><FaTimes size={14} /></button>
              </div>
              <div className="space-y-4">
                {["S", "A", "B"].map((tier) => (
                  <div key={tier}>
                    <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: TIER_META[tier].color }}>
                      {TIER_META[tier].label}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {(playersByTier[tier] || []).map((p) => (
                        <button key={p.id} onClick={() => handleManualNominate(p.id)} disabled={busy}
                          className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-800 hover:bg-red-600/20 border border-gray-700 hover:border-red-600/50 rounded-lg text-sm transition-colors text-left">
                          <span className="text-white font-semibold truncate">{p.name}</span>
                          <span className="text-gray-500 text-xs tabular-nums flex-shrink-0">{fmt(p.basePrice)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Captains */}
          <section className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-sm font-black uppercase tracking-widest flex items-center gap-2">
                <FaUsers size={13} /> Captains ({captains.length})
              </h2>
              <button onClick={handleRegenAllTokens}
                className="text-xs text-gray-500 hover:text-red-400 transition-colors flex items-center gap-1">
                <FaRedoAlt size={10} /> Regenerate ALL tokens
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {captains.map((c) => {
                const img = CAPTAIN_IMAGES[c.name];
                const fullBudget = session.settings?.budget || 120000;
                const pctSpent = Math.max(0, Math.min(100, ((fullBudget - c.budgetRemaining) / fullBudget) * 100));
                const slotsFilled = (c.roster || []).length;
                return (
                  <div key={c.id} className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-900 border-2 flex-shrink-0" style={{ borderColor: c.color + "80" }}>
                      {img ? <img src={img} alt={c.name} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : <div className="w-full h-full flex items-center justify-center text-white font-black">{c.name.charAt(0)}</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h4 className="text-white font-bold">{c.name}</h4>
                        <span className="text-xs text-gray-500 font-semibold">{slotsFilled}/{session.settings?.rosterSize || 3}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-green-400 font-bold tabular-nums text-sm">{fmt(c.budgetRemaining)}</span>
                        <div className="flex-1 h-1.5 bg-gray-900 rounded-full overflow-hidden">
                          <div className="h-full bg-red-600 transition-all" style={{ width: pctSpent + "%" }} />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => copyCaptainLink(c)} className="flex items-center gap-1 px-2 py-1 bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded text-xs text-gray-400 hover:text-white transition-colors">
                          <FaCopy size={9} /> {copiedId === c.id ? "Copied!" : "Copy Link"}
                        </button>
                        <button onClick={() => handleRegenCaptainToken(c.id)} className="px-2 py-1 bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded text-xs text-gray-500 hover:text-red-400 transition-colors">
                          <FaRedoAlt size={9} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Recent bids */}
          {bids.length > 0 && (
            <section className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
              <h2 className="text-sm font-black uppercase tracking-widest mb-3">Recent Bids</h2>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {bids.map((b) => {
                  const cap = captains.find((c) => c.id === b.captainId);
                  const pl = players.find((p) => p.id === b.playerId);
                  return (
                    <div key={b.id} className="flex items-center justify-between text-sm py-1 border-b border-gray-800/60">
                      <span className="text-gray-400">
                        <span className="text-white font-bold">{cap?.name || b.captainId}</span> → {pl?.name || "?"}
                      </span>
                      <span className="text-green-400 tabular-nums">{fmt(b.amount)}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default DraftWarsPanel;
